
import * as _reactDom from 'react-dom';
import * as _react from 'react';

import _uniqueId2 from 'lodash/uniqueId.js';

import _noop2 from 'lodash/noop.js';

import _omit2 from 'lodash/omit.js';

import _spatialNavigation, { ROOT_FOCUS_KEY } from './spatialNavigation.js';

/* Inline replacements for the recompose HOCs the npm build composed with
   (compose / withStateHandlers / withHandlers / lifecycle / pure / mapProps).
   Recompose still routes through React.createFactory, which React 18 deprecates
   (removed in 19) and warns about on every focusable type creation. */
var compose = function compose() {
  for (var _len = arguments.length, funcs = new Array(_len), _key = 0; _key < _len; _key++) { funcs[_key] = arguments[_key]; }
  return funcs.reduce(function (a, b) { return function (arg) { return a(b(arg)); }; });
};

var pure = _react.memo;

var withHandlers = function withHandlers(handlersFactory) {
  return function (Component) {
    return function WithHandlers(props) {
      var handlers = {};
      var factory = typeof handlersFactory === 'function' ? handlersFactory(props) : handlersFactory;
      Object.keys(factory).forEach(function (key) {
        handlers[key] = function () {
          for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) { args[_key2] = arguments[_key2]; }
          var handler = factory[key](props);
          return handler.apply(undefined, args);
        };
      });
      return _react.createElement(Component, Object.assign({}, props, handlers));
    };
  };
};

var withStateHandlers = function withStateHandlers(initialState, handlersFactory) {
  return function (Component) {
    return function WithStateHandlers(props) {
      var _useState = _react.useState(function () {
        return typeof initialState === 'function' ? initialState(props) : initialState;
      }), state = _useState[0], setState = _useState[1];

      var handlers = {};
      var factory = typeof handlersFactory === 'function' ? handlersFactory(props, state) : handlersFactory;
      Object.keys(factory).forEach(function (key) {
        handlers[key] = function () {
          for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) { args[_key3] = arguments[_key3]; }
          setState(function (prevState, prevProps) {
            var update = factory[key](prevState, prevProps).apply(undefined, args);
            return Object.assign({}, prevState, update);
          });
        };
      });

      return _react.createElement(Component, Object.assign({}, props, state, handlers));
    };
  };
};

var lifecycle = function lifecycle(spec) {
  return function (Component) {
    var Lifecycle = /*#__PURE__*/ (function (_React$Component) {
      _inherits(Lifecycle, _React$Component);

      function Lifecycle(props) {
        var _this;

        _classCallCheck(this, Lifecycle);

        _this = _possibleConstructorReturn(this, _React$Component.call(this, props));

        return _this;
      }

      Object.assign(Lifecycle.prototype, spec);
      Lifecycle.prototype.render = function render() {
        return _react.createElement(Component, this.props);
      };

      return Lifecycle;
    })(_react.Component);

    return Lifecycle;
  };
};

function _inherits(subClass, superClass) { if (typeof superClass !== 'function' && superClass !== null) { throw new TypeError('Super expression must either be null or a function, not ' + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError('Cannot call a class as a function'); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === 'object' || typeof call === 'function') ? call : self; }

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; } /* eslint-disable react/no-find-dom-node */


/**
 * Focusable context (modern API). Replaces the original recompose
 * withContext/getContext legacy-context pair: every withFocusable instance
 * provides its realFocusKey as parentFocusKey to descendant focusables.
 * Legacy childContextTypes is deprecated in React 18 and removed in React 19.
 */
var FocusableContext = _react.createContext({ parentFocusKey: undefined });

var withFocusableParentKey = function withFocusableParentKey(Component) {
  return function FocusableContextConsumer(props) {
    return _react.createElement(FocusableContext.Consumer, null, function (context) {
      return _react.createElement(Component, Object.assign({}, props, {
        parentFocusKey: context && context.parentFocusKey
      }));
    });
  };
};

var withFocusableProvider = function withFocusableProvider(Component) {
  return function FocusableContextProvider(props) {
    return _react.createElement(FocusableContext.Provider, {
      value: { parentFocusKey: props.realFocusKey }
    }, _react.createElement(Component, props));
  };
};

var omitProps = function omitProps(keys) {
  return function (Component) {
    return function OmitProps(props) {
      return _react.createElement(Component, (0, _omit2)(props, keys));
    };
  };
};

var withFocusable = function withFocusable() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$forgetLastFocuse = _ref.forgetLastFocusedChild,
      configForgetLastFocusedChild = _ref$forgetLastFocuse === undefined ? false : _ref$forgetLastFocuse,
      _ref$trackChildren = _ref.trackChildren,
      configTrackChildren = _ref$trackChildren === undefined ? false : _ref$trackChildren,
      configAutoRestoreFocus = _ref.autoRestoreFocus,
      _ref$blockNavigationO = _ref.blockNavigationOut,
      configBlockNavigationOut = _ref$blockNavigationO === undefined ? false : _ref$blockNavigationO;

  return compose(withFocusableParentKey, withStateHandlers(function (_ref2) {
    var focusKey = _ref2.focusKey,
        parentFocusKey = _ref2.parentFocusKey;

    var realFocusKey = focusKey || (0, _uniqueId2)('sn:focusable-item-');

    return {
      realFocusKey: realFocusKey,

      /**
       * This method is used to imperatively set focus to a component.
       * It is blocked in the Native mode because the native engine decides what to focus by itself.
       */
      setFocus: _spatialNavigation.isNativeMode() ? _noop2 : _spatialNavigation.setFocus.bind(null, realFocusKey),

      navigateByDirection: _spatialNavigation.navigateByDirection,

      /**
       * In Native mode this is the only way to mark component as focused.
       * This method always steals focus onto current component no matter which arguments are passed in.
       */
      stealFocus: _spatialNavigation.setFocus.bind(null, realFocusKey, realFocusKey),
      focused: false,
      hasFocusedChild: false,
      parentFocusKey: parentFocusKey || ROOT_FOCUS_KEY
    };
  }, {
    onUpdateFocus: function onUpdateFocus() {
      return function () {
        var focused = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
        return {
          focused: focused
        };
      };
    },
    onUpdateHasFocusedChild: function onUpdateHasFocusedChild() {
      return function () {
        var hasFocusedChild = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
        return {
          hasFocusedChild: hasFocusedChild
        };
      };
    }
  }),

  /**
   * Propagate own 'focusKey' as a 'parentFocusKey' to it's children
   */
  withFocusableProvider, withHandlers({
    onEnterPressHandler: function onEnterPressHandler(_ref4) {
      var _ref4$onEnterPress = _ref4.onEnterPress,
          onEnterPress = _ref4$onEnterPress === undefined ? _noop2 : _ref4$onEnterPress,
          rest = _objectWithoutProperties(_ref4, ['onEnterPress']);

      return function (details) {
        onEnterPress(rest, details);
      };
    },
    onEnterReleaseHandler: function onEnterReleaseHandler(_ref5) {
      var _ref5$onEnterRelease = _ref5.onEnterRelease,
          onEnterRelease = _ref5$onEnterRelease === undefined ? _noop2 : _ref5$onEnterRelease,
          rest = _objectWithoutProperties(_ref5, ['onEnterRelease']);

      return function () {
        onEnterRelease(rest);
      };
    },
    onArrowPressHandler: function onArrowPressHandler(_ref6) {
      var _ref6$onArrowPress = _ref6.onArrowPress,
          onArrowPress = _ref6$onArrowPress === undefined ? _noop2 : _ref6$onArrowPress,
          rest = _objectWithoutProperties(_ref6, ['onArrowPress']);

      return function (direction, details) {
        return onArrowPress(direction, rest, details);
      };
    },
    onBecameFocusedHandler: function onBecameFocusedHandler(_ref7) {
      var _ref7$onBecameFocused = _ref7.onBecameFocused,
          onBecameFocused = _ref7$onBecameFocused === undefined ? _noop2 : _ref7$onBecameFocused,
          rest = _objectWithoutProperties(_ref7, ['onBecameFocused']);

      return function (layout, details) {
        onBecameFocused(layout, rest, details);
      };
    },
    onBecameBlurredHandler: function onBecameBlurredHandler(_ref8) {
      var _ref8$onBecameBlurred = _ref8.onBecameBlurred,
          onBecameBlurred = _ref8$onBecameBlurred === undefined ? _noop2 : _ref8$onBecameBlurred,
          rest = _objectWithoutProperties(_ref8, ['onBecameBlurred']);

      return function (layout, details) {
        onBecameBlurred(layout, rest, details);
      };
    },
    pauseSpatialNavigation: function pauseSpatialNavigation() {
      return _spatialNavigation.pause;
    },
    resumeSpatialNavigation: function resumeSpatialNavigation() {
      return _spatialNavigation.resume;
    },
    updateAllSpatialLayouts: function updateAllSpatialLayouts() {
      return _spatialNavigation.updateAllLayouts;
    }
  }), lifecycle({
    componentDidMount: function componentDidMount() {
      var _props = this.props,
          focusKey = _props.realFocusKey,
          parentFocusKey = _props.parentFocusKey,
          preferredChildFocusKey = _props.preferredChildFocusKey,
          _props$forgetLastFocu = _props.forgetLastFocusedChild,
          forgetLastFocusedChild = _props$forgetLastFocu === undefined ? false : _props$forgetLastFocu,
          onEnterPressHandler = _props.onEnterPressHandler,
          onEnterReleaseHandler = _props.onEnterReleaseHandler,
          onArrowPressHandler = _props.onArrowPressHandler,
          onBecameFocusedHandler = _props.onBecameFocusedHandler,
          onBecameBlurredHandler = _props.onBecameBlurredHandler,
          onUpdateFocus = _props.onUpdateFocus,
          onUpdateHasFocusedChild = _props.onUpdateHasFocusedChild,
          trackChildren = _props.trackChildren,
          _props$focusable = _props.focusable,
          focusable = _props$focusable === undefined ? true : _props$focusable,
          _props$autoRestoreFoc = _props.autoRestoreFocus,
          autoRestoreFocus = _props$autoRestoreFoc === undefined ? true : _props$autoRestoreFoc,
          _props$blockNavigatio = _props.blockNavigationOut,
          blockNavigationOut = _props$blockNavigatio === undefined ? false : _props$blockNavigatio;


      var node = _spatialNavigation.isNativeMode() ? this : (0, _reactDom.findDOMNode)(this);

      _spatialNavigation.addFocusable({
        focusKey: focusKey,
        node: node,
        parentFocusKey: parentFocusKey,
        preferredChildFocusKey: preferredChildFocusKey,
        onEnterPressHandler: onEnterPressHandler,
        onEnterReleaseHandler: onEnterReleaseHandler,
        onArrowPressHandler: onArrowPressHandler,
        onBecameFocusedHandler: onBecameFocusedHandler,
        onBecameBlurredHandler: onBecameBlurredHandler,
        onUpdateFocus: onUpdateFocus,
        onUpdateHasFocusedChild: onUpdateHasFocusedChild,
        forgetLastFocusedChild: configForgetLastFocusedChild || forgetLastFocusedChild,
        trackChildren: configTrackChildren || trackChildren,
        blockNavigationOut: configBlockNavigationOut || blockNavigationOut,
        autoRestoreFocus: configAutoRestoreFocus !== undefined ? configAutoRestoreFocus : autoRestoreFocus,
        focusable: focusable
      });
    },
    componentDidUpdate: function componentDidUpdate() {
      var _props2 = this.props,
          focusKey = _props2.realFocusKey,
          preferredChildFocusKey = _props2.preferredChildFocusKey,
          _props2$focusable = _props2.focusable,
          focusable = _props2$focusable === undefined ? true : _props2$focusable,
          _props2$blockNavigati = _props2.blockNavigationOut,
          blockNavigationOut = _props2$blockNavigati === undefined ? false : _props2$blockNavigati,
          onEnterPressHandler = _props2.onEnterPressHandler,
          onEnterReleaseHandler = _props2.onEnterReleaseHandler,
          onArrowPressHandler = _props2.onArrowPressHandler,
          onBecameFocusedHandler = _props2.onBecameFocusedHandler,
          onBecameBlurredHandler = _props2.onBecameBlurredHandler;


      var node = _spatialNavigation.isNativeMode() ? this : (0, _reactDom.findDOMNode)(this);

      _spatialNavigation.updateFocusable(focusKey, {
        node: node,
        preferredChildFocusKey: preferredChildFocusKey,
        focusable: focusable,
        blockNavigationOut: configBlockNavigationOut || blockNavigationOut,
        /* Handlers are captured at addFocusable time; refresh them here so props
           that change after mount (e.g. onEnterPress toggling noOp↔real handler)
           take effect without a remount. Matches upstream 2.12.9? No — upstream
           never refreshes them; this is the local fix. */
        onEnterPressHandler: onEnterPressHandler,
        onEnterReleaseHandler: onEnterReleaseHandler,
        onArrowPressHandler: onArrowPressHandler,
        onBecameFocusedHandler: onBecameFocusedHandler,
        onBecameBlurredHandler: onBecameBlurredHandler
      });
    },
    componentWillUnmount: function componentWillUnmount() {
      var focusKey = this.props.realFocusKey;


      _spatialNavigation.removeFocusable({
        focusKey: focusKey
      });
    }
  }), pure, omitProps(['onBecameFocusedHandler', 'onBecameBlurredHandler', 'onEnterPressHandler', 'onEnterReleaseHandler', 'onArrowPressHandler', 'onUpdateFocus', 'onUpdateHasFocusedChild', 'forgetLastFocusedChild', 'trackChildren', 'autoRestoreFocus']));
};

export default withFocusable;